import { useContext } from 'react';
import axios from 'src/axios';
import { AppContext } from 'src/store/ContextStore';
import { APICONSTANTS } from 'src/components/Constants';
import { EVENTS } from 'src/components/admin/appReducer';
import { SuggestionFamilyContext } from 'src/components/UI/clinicalSuggestions/SuggestionFamilyContext';
import { useMemberRowSync } from 'src/components/pedigree-chart/pedigree-tool/memberRowSyncContext';
import { replaceMemberGenotypesAfter } from 'src/components/pedigree-chart/pedigree-sidebar/sections/genotypes-menu/memberGenotypesRow';
import { notifyTestResultsChanged } from './useStructuredTestResults';

// Withdrawing a lab report as entered in error (TGCLOUD-1866, Phase 2 rework):
// POST /test-results/:id/entered-in-error marks the report's record, and removes from the Test and
// from the person's genotype record what came ONLY from it (backend labReportWithdrawal.js).
//
// THE CHART MUST TAKE THE ANSWER. The pedigree autosave writes each person's whole row, genotypes
// included, so a removal the chart never reads would be written straight back by the next save.
// The answer carries the person's genotypes as stored after the removal; they REPLACE the row's
// (replaceMemberGenotypesAfter, under the autosave hold) and the open person's in the sidebar,
// then the family is re-read with its clinical data, as the Suggested Clinical Terms accept path
// does. The Test's own changed fields (`testNote`) are the caller's to take into the open form:
// a later Save would otherwise write the removed genes and variants back.
//
// WHY, AND WHERE IT GOES (Phase 3, "Undo after linking"). `reason` is `withdrawn` (as before),
// `wrong-patient` (back to the lab report inbox with this person rejected on the item) or
// `wrong-test` (back to the inbox, still matched to this person). The withdrawal itself is the same
// for all three; the server returns the report to the inbox after it, and says so in `inbox`.

export function useLabReportWithdrawal({ familyMemberId, isProband }) {
  const { content, dispatchUpdate } = useContext(AppContext) || {};
  const family = useContext(SuggestionFamilyContext) || {};
  const rowSync = useMemberRowSync();
  const users = content?.pedigreeSelectedUsers || {};

  // The open person's genotypes in the sidebar, REPLACED by the stored list (the answer is the
  // person's whole list), with the stale aggregates stripped so the reducer recomputes them.
  const putOnSelectedPerson = (whole) => {
    if (!whole || !Array.isArray(whole.genotypes) || !dispatchUpdate) return;
    /* eslint-disable no-unused-vars */
    const {
      diagnoses: _d,
      phenotypes: _p,
      genotypes: _g,
      ...usersBase
    } = users;
    /* eslint-enable no-unused-vars */
    const replaced = (person) => ({ ...person, genotypes: whole.genotypes });
    const who = whole.familyMemberId || familyMemberId;
    dispatchUpdate({
      type: EVENTS.SET_PEDIGREE_SELECTED_USER,
      value:
        whole.isProband ?? isProband
          ? { ...usersBase, selectedUser: replaced(users.selectedUser) }
          : {
              ...usersBase,
              family: (users.family || []).map((m) =>
                m.familyMemberId === who ? replaced(m) : m
              )
            }
    });
  };

  /**
   * @param {string} labReportId the lab report record to withdraw
   * @param {{ testNoteId?: string, reason?: 'withdrawn'|'wrong-patient'|'wrong-test' }} [options]
   * @returns {Promise<object>} the server's answer: { removed, toReview, testNote, genotypes,
   *   reason, inbox?, ... }
   */
  const withdraw = async (labReportId, { testNoteId, reason } = {}) => {
    const data = await replaceMemberGenotypesAfter({
      rowSync,
      reason: 'lab-report-withdrawal',
      write: async () => {
        const res = await axios.post(
          APICONSTANTS.testResultEnteredInError.replace(
            ':id',
            encodeURIComponent(labReportId)
          ),
          reason ? { reason } : undefined
        );
        return res?.data || {};
      }
    });
    if (data?.genotypes) putOnSelectedPerson(data.genotypes);
    try {
      await family.refreshFamily?.();
    } catch (err) {
      console.error('Failed to refresh the family after a withdrawal:', err);
    }
    notifyTestResultsChanged({ testNoteId });
    return data;
  };

  return { withdraw };
}
